import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trophy, Plus, ArrowRight, Store, Clock, Flag } from "lucide-react";
import { api } from "../lib/api";

function timeLeft(endDate) {
  const ms = new Date(endDate).getTime() - Date.now();
  if (ms <= 0) return "ended";
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hours = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  if (days > 0) return `${days}d ${hours}h left`;
  const mins = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${mins}m left`;
}

export default function Rounds() {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.listRounds().then((r) => {
      setRounds(r);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-8" data-testid="rounds-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="lp-section-label">Competitive launches</div>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-2">Launch Rounds</h1>
          <p className="text-sm text-gray-600 mt-2 max-w-xl">
            Pit upcoming products against each other. Only the top-voted winners advance to pre-order when the round ends.
          </p>
        </div>
        <Link to="/app/rounds/new" className="lp-btn-primary" data-testid="create-round-btn">
          <Plus size={16} />
          New round
        </Link>
      </div>

      <div className="lp-card overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium tracking-tight">All rounds</h2>
            <p className="text-xs text-gray-500 mt-1">Active rounds appear on the storefront automatically.</p>
          </div>
          <span className="text-xs text-gray-500">{rounds.length} total</span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-gray-500 text-sm">Loading rounds…</div>
        ) : rounds.length === 0 ? (
          <div className="p-16 text-center" data-testid="rounds-empty-state">
            <div className="w-14 h-14 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center mx-auto mb-4">
              <Trophy size={22} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-medium tracking-tight">No launch rounds yet</h3>
            <p className="text-sm text-gray-500 mt-2 max-w-sm mx-auto">
              Group 2+ products into a round, set how many winners advance, and let customers pick the launch order.
            </p>
            <button
              onClick={() => navigate("/app/rounds/new")}
              className="lp-btn-primary mt-6"
              data-testid="empty-create-round-btn"
            >
              <Plus size={16} />
              Create your first round
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="lp-table" data-testid="rounds-table">
              <thead>
                <tr>
                  <th>Round</th>
                  <th>Status</th>
                  <th>Winners</th>
                  <th>Ends</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rounds.map((r) => (
                  <tr key={r.id} data-testid={`round-row-${r.id}`}>
                    <td>
                      <div className="font-medium text-gray-900">{r.name}</div>
                      {r.description && <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{r.description}</div>}
                    </td>
                    <td>
                      {r.status === "active" ? (
                        <span className="lp-badge lp-badge-voting" data-testid={`round-status-${r.id}`}>Active</span>
                      ) : (
                        <span className="lp-badge lp-badge-closed" data-testid={`round-status-${r.id}`}>Completed</span>
                      )}
                    </td>
                    <td className="text-sm text-gray-900 font-medium">Top {r.winnersCount}</td>
                    <td>
                      <div className="flex items-center gap-1.5 text-sm text-gray-700">
                        <Clock size={13} className="text-gray-400" />
                        <span data-testid={`round-time-${r.id}`}>
                          {r.status === "active" ? timeLeft(r.endDate) : "—"}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{new Date(r.endDate).toLocaleString()}</div>
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/storefront/rounds/${r.id}`}
                          className="lp-btn-secondary text-xs"
                          data-testid={`round-storefront-${r.id}`}
                        >
                          <Store size={14} /> Storefront
                        </Link>
                        <Link
                          to={`/app/rounds/${r.id}`}
                          className="lp-btn-primary text-xs"
                          data-testid={`round-manage-${r.id}`}
                        >
                          {r.status === "active" ? (<>Manage <ArrowRight size={14} /></>) : (<><Flag size={14} /> Review</>)}
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
